import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import KYCGate from "@/components/KYCGate";
import MonoConnectButton from "@/components/MonoConnectButton";
import InvoiceLoanPath from "@/components/InvoiceLoanPath";
import { api, API, fmtNGN, fmtBps, formatErr, fmtDate } from "@/lib/api";
import { Brain, Sparkle, ShieldCheck, CheckCircle, CircleNotch, Bank, FileText, ArrowRight, Warning, Lightning, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BackArrow, ReviewPanel } from "@/components/StepNav";

const STATE_FLOW = [
  "consent",
  "form",
  "discovering",
  "aip_offered",
  "aip_accepted",       // waiting for admin
  "final_approved",     // accept offer
  "ispo_pending",       // review & sign ISPO
  "ispo_accepted",      // DD setup
  "dd_setup_pending",   // waiting admin verify
  "dd_verified",        // waiting disburse
  "disbursed",
  "declined",
];

const STATE_LABEL = {
  consent: "Consent",
  form: "Application",
  discovering: "Processing",
  aip_offered: "Offer available",
  aip_accepted: "Under review",
  final_approved: "Loan offer ready",
  ispo_pending: "Sign standing payment order",
  ispo_accepted: "Direct Debit setup",
  offer_accepted: "Direct Debit setup",
  dd_setup_pending: "Finalising",
  dd_verified: "Awaiting disbursement",
  disbursed: "Disbursed",
  declined: "Declined",
};

const BLANK_FORM = { school_id: "", amount: 200000, tenor_months: 6, purpose: "School fees payment", remark: "" };
const RAIL = ["consent", "form", "aip_offered", "aip_accepted", "final_approved", "ispo_pending", "ispo_accepted", "dd_verified", "disbursed"];
const RAIL_ALIAS = { discovering: "form", offer_accepted: "ispo_accepted", dd_setup_pending: "ispo_accepted" };
const railIdx = (stage) => RAIL.indexOf(RAIL_ALIAS[stage] || stage);

export default function ParentLoans() {
  const [schools, setSchools] = useState([]);
  const [loans, setLoans] = useState([]);
  const [active, setActive] = useState(null); // { state, loan?, ... }
  const [collection, setCollection] = useState(null);

  const reload = async () => {
    const [c, l, b] = await Promise.all([
      api.get("/parent/partner-schools").then((r) => r.data),
      api.get("/loans/mine").then((r) => r.data),
      api.get("/loans/ascentremit-collection-account").then((r) => r.data).catch(() => null),
    ]);
    setSchools(c);
    setLoans(l);
    setCollection(b);
  };
  useEffect(() => { reload(); }, []);

  const startNew = () => setActive({ stage: "consent", form: { ...BLANK_FORM, consent_data_privacy: false, consent_bank_access: false } });

  return (
    <DashboardShell title="School-fee loans" subtitle="Apply for a loan, track your active applications, or make an early repayment.">
      <KYCGate>
        {!active && (
          <div className="space-y-6">
            <button onClick={startNew} className="btn-primary" data-testid="start-loan-button">
              <Brain size={16} weight="fill" /> Start a new loan application
            </button>

            <div className="card">
              <h2 className="font-display text-xl font-medium mb-4">My applications</h2>
              {loans.length === 0 ? (
                <p className="text-sm text-[#4F5D4F] py-6 text-center">No applications yet.</p>
              ) : (
                <div className="space-y-3">
                  {loans.map((l) => (
                    <div key={l.id} className="border border-[#E5E3DB] rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`loan-row-${l.id}`}>
                      <div>
                        <div className="font-display text-lg font-medium">{fmtNGN(l.amount)} · {l.tenor_months} mo</div>
                        <div className="text-sm text-[#4F5D4F]">Applied {fmtDate(l.created_at)} · Band {l.ai_risk_band}</div>
                        <LoanDocs loan={l} />
                      </div>
                      <StateBadge state={l.state || l.status} />
                      <button onClick={() => setActive({ stage: l.state, loan: l })} className="btn-outline !py-2 !px-4 text-sm" data-testid={`continue-loan-${l.id}`}>
                        Continue <ArrowRight size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {active && <Wizard active={active} setActive={setActive} reload={reload} schools={schools} collection={collection} />}
      </KYCGate>
    </DashboardShell>
  );
}

function LoanDocs({ loan }) {
  const hasOffer = !!loan.final_approved_at;
  const hasIspo = loan.ispo?.status === "accepted";
  if (!hasOffer && !hasIspo) return null;
  const cls = "inline-flex items-center gap-1 text-xs font-semibold text-[#0A3622] hover:underline";
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2" data-testid={`loan-docs-${loan.id}`}>
      {hasOffer && (
        <a href={`${API}/loans/${loan.id}/offer-letter.pdf`} target="_blank" rel="noreferrer" className={cls} data-testid={`loan-offer-pdf-${loan.id}`}>
          <DownloadSimple size={14} weight="bold" /> Offer letter (PDF)
        </a>
      )}
      {hasIspo && (
        <a href={`${API}/loans/${loan.id}/ispo.pdf`} target="_blank" rel="noreferrer" className={cls} data-testid={`loan-ispo-pdf-${loan.id}`}>
          <DownloadSimple size={14} weight="bold" /> Signed ISPO (PDF)
        </a>
      )}
    </div>
  );
}

function StateBadge({ state }) {
  const map = {
    aip_offered: ["badge-info", "AIP offered"],
    aip_accepted: ["badge-warning", "Awaiting officer"],
    final_approved: ["badge-info", "Offer ready"],
    offer_accepted: ["badge-warning", "DD setup"],
    dd_setup_pending: ["badge-warning", "DD verifying"],
    dd_verified: ["badge-info", "Ready to disburse"],
    disbursed: ["badge-success", "Disbursed"],
    declined: ["badge-danger", "Declined"],
    approved: ["badge-success", "Approved"],
    pending: ["badge-warning", "Pending"],
  };
  const [c, l] = map[state] || ["badge-neutral", state];
  return <span className={`badge ${c}`} data-testid={`state-badge-${state}`}>{l}</span>;
}

function StepRail({ idx }) {
  if (idx < 0) return null;
  const pct = Math.round(((idx + 1) / RAIL.length) * 100);
  return (
    <div className="mb-7" data-testid="wizard-rail">
      <div className="flex items-center justify-between text-xs text-[#4F5D4F] mb-2">
        <span className="font-semibold uppercase tracking-[0.14em]" data-testid="wizard-step-label">Step {idx + 1} of {RAIL.length}</span>
        <span className="font-mono">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#E5E3DB] overflow-hidden">
        <div className="h-full rounded-full bg-[#0A3622] transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Wizard({ active, setActive, reload, schools, collection }) {
  const stage = active.stage;
  const loan = active.loan;
  const [viewIdx, setViewIdx] = useState(null);
  useEffect(() => { setViewIdx(null); }, [stage]);
  const curIdx = railIdx(stage);
  const shownIdx = viewIdx ?? curIdx;
  const back = () => (loan ? setViewIdx(shownIdx - 1) : setActive({ ...active, stage: RAIL[shownIdx - 1] }));
  const next = () => setViewIdx(shownIdx + 1 >= curIdx ? null : shownIdx + 1);
  return (
    <div className="card !p-7" data-testid="loan-wizard">
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="overline">Loan application</div>
          <h2 className="font-display text-2xl mt-1 font-medium">{STATE_LABEL[viewIdx !== null ? RAIL[viewIdx] : stage] || "Application"}</h2>
        </div>
        <button onClick={() => setActive(null)} className="btn-ghost text-sm" data-testid="wizard-close">Close</button>
      </div>
      <StepRail idx={shownIdx} />
      {shownIdx > 0 && stage !== "discovering" && <BackArrow onClick={back} testid="loan-back-arrow" />}

      {viewIdx !== null ? <LoanReview idx={viewIdx} loan={loan} schools={schools} onNext={next} /> : <>
      {stage === "consent" && <StepConsent active={active} setActive={setActive} />}
      {stage === "form" && <StepForm active={active} setActive={setActive} schools={schools} reload={reload} />}
      {(stage === "aip_offered") && <StepAIP loan={loan} setActive={setActive} reload={reload} />}
      {(stage === "aip_accepted") && <StepWaitingAdmin loan={loan} />}
      {(stage === "final_approved") && <StepOffer loan={loan} setActive={setActive} reload={reload} collection={collection} />}
      {(stage === "ispo_pending") && <StepISPO loan={loan} setActive={setActive} reload={reload} />}
      {(stage === "ispo_accepted" || stage === "offer_accepted" || stage === "dd_setup_pending") && <StepDD loan={loan} setActive={setActive} reload={reload} collection={collection} />}
      {(stage === "dd_verified") && <StepAwaitingDisburse loan={loan} />}
      {(stage === "disbursed") && <StepDisbursed loan={loan} setActive={setActive} />}
      {(stage === "declined") && <StepDeclined loan={loan} setActive={setActive} />}
      </>}
    </div>
  );
}

function StepConsent({ active, setActive }) {
  const f = active.form;
  const ok = f.consent_data_privacy && f.consent_bank_access;
  const setConsent = (k, v) => setActive({ ...active, form: v ? { ...f, [k]: v } : { ...BLANK_FORM, consent_data_privacy: f.consent_data_privacy, consent_bank_access: f.consent_bank_access, [k]: v } });
  return (
    <div data-testid="step-consent">
      <p className="text-sm text-[#4F5D4F] mb-5">
        As part of this application, Ascentremit will perform a credit assessment that requires <strong>read-only access to the bank account you link</strong> through our partner Mono. We analyse <strong>6 months of transactions</strong> from that account, and use your verified <strong>BVN and NIN</strong> to confirm it's you. Please review and accept both consents below.
      </p>

      <div className="space-y-4">
        <ConsentCard
          icon={<ShieldCheck size={20} weight="fill" />}
          title="Data Privacy Policy"
          body="I have read and accept Ascentremit's Data Privacy Policy. I understand that all personal data collected is encrypted, stored under NDPR-compliant standards, and only used for the purposes of credit assessment, loan administration, and regulatory reporting."
          checked={f.consent_data_privacy}
          onChange={(v) => setConsent("consent_data_privacy", v)}
          testId="consent-privacy"
        />
        <ConsentCard
          icon={<Bank size={20} weight="fill" />}
          title="Bank Access & Statement Aggregation"
          body="I authorise Ascentremit, through Mono, to read 6 months of transactions from the bank account I link in this application. I understand the AI scoring engine will analyse these transactions as part of this loan decision only."
          checked={f.consent_bank_access}
          onChange={(v) => setConsent("consent_bank_access", v)}
          testId="consent-bank"
        />
      </div>

      <div className="flex gap-3 mt-7">
        <button onClick={() => setActive(null)} className="btn-ghost text-sm">Cancel</button>
        <button disabled={!ok} onClick={() => setActive({ ...active, stage: "form" })} className="btn-primary disabled:opacity-50" data-testid="consent-continue">
          I agree — Continue <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function ConsentCard({ icon, title, body, checked, onChange, testId }) {
  return (
    <label className={`flex gap-4 p-5 rounded-2xl border cursor-pointer transition-all ${checked ? "border-[#0A3622] bg-[#E2F3EB]/30" : "border-[#E5E3DB] bg-white"}`} data-testid={testId}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 w-5 h-5 accent-[#0A3622]" />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          <div className={`w-9 h-9 rounded-xl grid place-items-center ${checked ? "bg-[#0A3622] text-[#F2B632]" : "bg-[#F0EFEA] text-[#4F5D4F]"}`}>{icon}</div>
          <div className="font-display text-base font-medium text-[#132A13]">{title}</div>
        </div>
        <p className="text-sm text-[#4F5D4F] leading-relaxed">{body}</p>
      </div>
    </label>
  );
}

function StepForm({ active, setActive, schools, reload }) {
  const [submitting, setSubmitting] = useState(false);
  const f = active.form;
  const update = (k, v) => setActive({ ...active, form: { ...f, [k]: v } });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Show "discovering" stage while backend processes
      setActive({ ...active, stage: "discovering" });
      const { mono_account_label, not_listed, invoice_payee, ...rest } = f; // eslint-disable-line no-unused-vars
      const payload = not_listed ? { ...rest, school_id: null, remark: null } : { ...rest, invoice_id: null };
      const { data } = await api.post("/loans/apply", payload);
      reload();
      setActive({ stage: data.state, loan: data });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
      setActive({ ...active, stage: "form" });
    } finally {
      setSubmitting(false);
    }
  };

  const maxAmount = f.invoice_payee ? Math.min(5000000, f.invoice_payee.amount_owed) : 5000000;
  const complete = (f.not_listed ? !!f.invoice_id : !!f.school_id) && f.amount >= 50000 && f.amount <= maxAmount
    && (f.purpose || "").trim() && !!f.mono_account_id;

  if (active.stage === "discovering") return <DiscoveringScreen />;

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="step-form">
      {!f.not_listed && (
        <Field label="School to be paid">
          <select className="input mt-1" required value={f.school_id} onChange={(e) => update("school_id", e.target.value)} data-testid="loan-school-select">
            <option value="">{schools.length ? "Choose a partner school" : "No partner schools available yet"}</option>
            {schools.map((s) => <option key={s.id} value={s.id}>{[s.school_name, s.city].filter(Boolean).join(" · ")}</option>)}
          </select>
        </Field>
      )}
      <label className="sm:col-span-2 flex items-center gap-2 text-sm text-[#0A3622] font-semibold cursor-pointer" data-testid="loan-not-listed-label">
        <input type="checkbox" checked={!!f.not_listed} data-testid="loan-school-not-listed"
          onChange={(e) => setActive({ ...active, form: { ...f, not_listed: e.target.checked, school_id: "", invoice_id: "", invoice_payee: null } })} />
        My child's school is not listed
      </label>
      {f.not_listed && (
        <InvoiceLoanPath
          onReset={() => setActive((a) => ({ ...a, form: { ...a.form, invoice_id: "", invoice_payee: null } }))}
          onConfirmed={({ invoice_id, payee }) => setActive((a) => ({ ...a, form: { ...a.form, invoice_id, invoice_payee: payee, amount: Math.min(a.form.amount || payee.amount_owed, payee.amount_owed) } }))}
        />
      )}
      <Field label="Loan amount (₦)">
        <input type="number" className="input mt-1" min="50000" max={f.invoice_payee ? Math.min(5000000, f.invoice_payee.amount_owed) : 5000000} required value={f.amount} onChange={(e) => update("amount", Number(e.target.value))} data-testid="loan-amount-input" />
      </Field>
      <Field label="Tenor (months)">
        <select className="input mt-1" value={f.tenor_months} onChange={(e) => update("tenor_months", Number(e.target.value))} data-testid="loan-tenor-select">
          {[1, 2, 3, 6, 9, 12].map((n) => <option key={n} value={n}>{`${n} months`}</option>)}
        </select>
      </Field>
      <Field label="Reason for loan">
        <input className="input mt-1" required value={f.purpose} onChange={(e) => update("purpose", e.target.value)} data-testid="loan-purpose-input" />
      </Field>
      {!f.not_listed && (
        <div className="sm:col-span-2">
          <Field label="Child's name (for the school's records)">
            <input className="input mt-1" maxLength={120} value={f.remark} onChange={(e) => update("remark", e.target.value)} placeholder="e.g. Chidi Okafor — Term 2 fees" data-testid="loan-remark-input" />
          </Field>
        </div>
      )}
      <div className="sm:col-span-2 p-4 rounded-2xl bg-[#F0EFEA]/60 border border-[#E5E3DB]" data-testid="loan-bank-link">
        <div className="text-sm font-semibold text-[#0A3622]">Link the bank account your income comes into</div>
        <p className="text-xs text-[#4F5D4F] mt-1 mb-3">We read 6 months of transactions (read-only, through Mono) to work out your pre-approval. We never see your bank password.</p>
        {f.mono_account_id ? (
          <div className="flex items-center gap-2 text-sm text-[#2D7A5D]" data-testid="loan-bank-linked">
            <CheckCircle size={16} weight="fill" /> {f.mono_account_label} linked
            <button type="button" className="text-xs underline text-[#0A3622] ml-2" onClick={() => setActive({ ...active, form: { ...f, mono_account_id: "", mono_account_label: "" } })}>Change</button>
          </div>
        ) : (
          <MonoConnectButton testid="loan-mono-connect" onLinked={(acc) => setActive({ ...active, form: { ...f, mono_account_id: acc.account_id, mono_account_label: `${acc.bank_name || "Bank"} ${acc.account_number_masked}` } })} />
        )}
      </div>
      <div className="sm:col-span-2 flex gap-3 mt-3">
        <button type="submit" disabled={submitting || !complete} className="btn-primary disabled:opacity-50" data-testid="loan-submit-button">
          <Brain size={16} weight="fill" /> {submitting ? "Processing…" : "Run credit assessment"}
        </button>
      </div>
    </form>
  );
}

function LoanReview({ idx, loan, schools, onNext }) {
  const dd = loan.dd_account || {};
  const panels = [
    ["Consent", [["Data Privacy Policy", "Accepted"], ["Bank access & statements", "Accepted"]]],
    ["Application", [["School", loan.school_name || schools.find((s) => s.id === loan.school_id)?.school_name || loan.payee?.school_name], ["Amount requested", fmtNGN(loan.requested_amount || loan.amount)], ["Tenor", `${loan.tenor_months} months`], ["Reason", loan.purpose], ["Child's name", loan.remark]]],
    ["Approval In Principle", [["Offered amount", fmtNGN(loan.amount)], ["Monthly", fmtNGN(loan.monthly_payment)], ["Risk band", loan.ai_risk_band && `Band ${loan.ai_risk_band}`], ["Accepted", fmtDate(loan.aip_accepted_at)]]],
    ["Credit officer review", [["Final approval", fmtDate(loan.final_approved_at)]]],
    ["Loan offer", [["Final amount", fmtNGN(loan.amount)], ["Tenor", `${loan.tenor_months} months`], ["Monthly", fmtNGN(loan.monthly_payment)], ["Total repayment", fmtNGN(loan.total_repayment)]]],
    ["Standing payment order", [["Reference", loan.ispo?.reference], ["Status", loan.ispo?.status === "accepted" ? "Signed" : loan.ispo?.status]]],
    ["Direct Debit", [["Bank", dd.bank_name], ["Account", dd.account_number && `****${dd.account_number.slice(-4)}`], ["Mandate reference", dd.mandate_reference]]],
    ["Direct Debit verified", [["Status", "Verified"]]],
  ];
  const [title, rows] = panels[idx] || ["Completed step", []];
  return <ReviewPanel title={title} rows={rows} locked onNext={onNext} testid={`loan-review-step-${idx + 1}`} />;
}

function DiscoveringScreen() {
  const stages = [
    "Reading your linked bank account…",
    "Analysing 6 months of transactions…",
    "AI scoring spending behaviour…",
    "Generating Approval In Principle…",
  ];
  const [s, setS] = useState(0);
  useEffect(() => { const t = setInterval(() => setS((x) => Math.min(x + 1, stages.length - 1)), 1000); return () => clearInterval(t); }, []); // eslint-disable-line
  return (
    <div className="text-center py-12" data-testid="discovering-screen">
      <CircleNotch size={48} className="animate-spin mx-auto text-[#0A3622]" />
      <h3 className="font-display text-2xl mt-5 font-medium">Verifying with your banks</h3>
      <ul className="mt-6 space-y-2 text-sm text-[#4F5D4F] max-w-md mx-auto">
        {stages.map((t, i) => (
          <li key={i} className={`flex items-center gap-2 ${i <= s ? "text-[#0A3622]" : "opacity-40"}`}>
            {i < s ? <CheckCircle size={16} weight="fill" className="text-[#2D7A5D]" /> : i === s ? <CircleNotch size={16} className="animate-spin" /> : <span className="w-4" />}
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepAIP({ loan, setActive, reload }) {
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try { await api.post(`/loans/${loan.id}/borrower-accept-aip`); toast.success("Accepted — application sent to credit officer"); reload(); setActive({ stage: "aip_accepted", loan: { ...loan, state: "aip_accepted" } }); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    setBusy(true);
    try { await api.post(`/loans/${loan.id}/borrower-decline-aip`); reload(); setActive(null); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="step-aip">
      <div className="bg-[#E2F3EB]/40 border border-[#2D7A5D]/20 rounded-2xl p-5 mb-5">
        <div className="overline !text-[#2D7A5D]">Approval In Principle</div>
        <p className="text-sm text-[#4F5D4F] mt-2">Based on bank statement analysis and AI scoring, you are pre-approved for the offer below. <strong>This is not a final approval</strong> — accepting sends the application to our credit officer for final review and a binding offer letter.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat k="Offered amount" v={fmtNGN(loan.amount)} tone="primary" testId="aip-offered" />
        <Stat k="Tenor" v={`${loan.tenor_months} mo`} />
        <Stat k="Monthly" v={fmtNGN(loan.monthly_payment)} />
        <Stat k="Total repayment" v={fmtNGN(loan.total_repayment)} />
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <Stat k="AI risk band" v={`Band ${loan.ai_risk_band}`} tone="info" />
        <Stat k="AI risk score" v={`${loan.ai_risk_score}/1000`} />
        <Stat k="APR" v={fmtBps(loan.interest_bps) + " / yr"} />
      </div>

      {loan.statements_summary && (
        <div className="bg-[#F9F7F4] rounded-xl p-4 text-sm text-[#4F5D4F] mb-5">
          <div className="overline mb-2">Bank-statement insights ({loan.statements_summary.accounts_analyzed} accounts, {loan.statements_summary.months_analyzed} mo)</div>
          <div className="grid sm:grid-cols-2 gap-1.5">
            <div>Avg inflow: <span className="font-mono text-[#132A13] font-semibold">{fmtNGN(loan.statements_summary.avg_inflow_ngn)}</span></div>
            <div>Avg outflow: <span className="font-mono text-[#132A13] font-semibold">{fmtNGN(loan.statements_summary.avg_outflow_ngn)}</span></div>
            <div>Salary consistency: <span className="font-mono text-[#132A13] font-semibold">{(loan.statements_summary.salary_consistency * 100).toFixed(0)}%</span></div>
            <div>Overdraft incidents: <span className="font-mono text-[#132A13] font-semibold">{loan.statements_summary.overdraft_count}</span></div>
          </div>
        </div>
      )}

      {loan.ai_reasons?.length > 0 && (
        <div className="bg-[#F9F7F4] rounded-xl p-4 text-sm mb-6">
          <div className="overline mb-2">AI reasoning</div>
          <ul className="space-y-1">{loan.ai_reasons.map((r, i) => <li key={i} className="text-[#4F5D4F]">• {r}</li>)}</ul>
        </div>
      )}

      <div className="flex gap-3">
        <button onClick={decline} disabled={busy} className="btn-outline" data-testid="aip-decline">Decline</button>
        <button onClick={accept} disabled={busy} className="btn-primary" data-testid="aip-accept">
          <CheckCircle size={16} weight="fill" /> Accept the offered amount
        </button>
      </div>
    </div>
  );
}

function StepWaitingAdmin({ loan }) {
  return (
    <div className="text-center py-10" data-testid="step-waiting-admin">
      <div className="w-16 h-16 rounded-full bg-[#FBF1DC] grid place-items-center mx-auto">
        <CircleNotch size={28} className="animate-spin text-[#B45309]" />
      </div>
      <h3 className="font-display text-2xl mt-5 font-medium">With our credit officer</h3>
      <p className="text-sm text-[#4F5D4F] mt-2 max-w-md mx-auto">
        You accepted the AIP of <strong>{fmtNGN(loan.amount)}</strong>. Our credit officer is conducting a final review and will email you a binding Loan Offer Letter shortly.
      </p>
    </div>
  );
}

function StepOffer({ loan, setActive, reload, collection }) {
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/loans/${loan.id}/borrower-accept-offer`);
      toast.success("Offer accepted — please review the Standing Payment Order");
      reload();
      setActive({ stage: "ispo_pending", loan: { ...loan, state: "ispo_pending", ispo: { reference: data.ispo_reference } } });
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="step-offer">
      <div className="bg-[#E2F3EB]/40 border border-[#2D7A5D]/20 rounded-2xl p-5 mb-5">
        <div className="flex items-center gap-2"><FileText size={18} className="text-[#2D7A5D]" weight="fill" /><div className="overline !text-[#2D7A5D]">Final Loan Offer Letter</div></div>
        <p className="text-sm text-[#4F5D4F] mt-2">Our credit officer has approved your final loan amount. By accepting below you commit to the binding terms; you'll then be asked to sign the Irrevocable Standing Payment Order before your account is activated.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat k="Final amount" v={fmtNGN(loan.amount)} tone="primary" testId="offer-final-amount" />
        <Stat k="Tenor" v={`${loan.tenor_months} mo`} />
        <Stat k="Monthly" v={fmtNGN(loan.monthly_payment)} />
        <Stat k="Total repayment" v={fmtNGN(loan.total_repayment)} />
      </div>

      <button onClick={accept} disabled={busy} className="btn-primary" data-testid="offer-accept">
        <CheckCircle size={16} weight="fill" /> Accept the final offer
      </button>
    </div>
  );
}

function StepISPO({ loan, setActive, reload }) {
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const ref = loan.ispo?.reference || "—";
  const accept = async () => {
    if (!confirmed) return toast.error("Please tick the acknowledgement box first");
    setBusy(true);
    try {
      await api.post(`/loans/${loan.id}/ispo/accept`);
      toast.success("ISPO signed — a copy has been emailed to you and the credit officer");
      reload();
      setActive({ stage: "ispo_accepted", loan: { ...loan, state: "ispo_accepted" } });
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    if (!window.confirm("Declining the standing payment order will close this loan application. Continue?")) return;
    setBusy(true);
    try {
      await api.post(`/loans/${loan.id}/ispo/decline`);
      toast.info("Application closed — a decline email has been sent");
      reload();
      setActive({ stage: "declined", loan: { ...loan, state: "declined" } });
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="step-ispo">
      <div className="bg-[#0A3622] text-white rounded-2xl p-6 mb-5">
        <div className="overline !text-[#F2B632]">Irrevocable Standing Payment Order</div>
        <h3 className="font-display text-2xl font-medium mt-2">Standing Payment Order · {ref}</h3>
        <p className="text-white/80 text-sm mt-3 leading-relaxed">
          I, <strong>{loan.applicant_name || "the borrower"}</strong>, hereby irrevocably authorise M.S Ascent Global Ventures Limited
          to debit my nominated bank account for the sum of
          <strong> {fmtNGN(loan.monthly_payment)}</strong> monthly, for <strong>{loan.tenor_months} consecutive months</strong>,
          toward repayment of the school-fee loan of <strong>{fmtNGN(loan.amount)}</strong> (total repayable {fmtNGN(loan.total_repayment)}).
        </p>
        <p className="text-white/80 text-sm mt-3 leading-relaxed">
          This authority remains in force until the loan is fully repaid and cannot be cancelled unilaterally.
          I acknowledge that failure to maintain sufficient balance will incur late-payment fees and may be reported to credit bureaux.
        </p>
      </div>

      <label className="flex items-start gap-3 p-4 border border-[#E5E3DB] rounded-xl mb-5 cursor-pointer hover:bg-[#F0EFEA]/50 transition-colors">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-1"
          data-testid="ispo-confirm-checkbox"
        />
        <span className="text-sm text-[#132A13]">
          I have read, understood, and agree to be bound by the Irrevocable Standing Payment Order above.
          I understand this is a legally binding instruction to my bank.
        </span>
      </label>

      <div className="flex gap-3">
        <button onClick={accept} disabled={busy || !confirmed} className="btn-primary flex-1" data-testid="ispo-accept">
          <CheckCircle size={16} weight="fill" /> Sign & accept
        </button>
        <button onClick={decline} disabled={busy} className="btn-outline" data-testid="ispo-decline">
          Decline application
        </button>
      </div>
    </div>
  );
}

function StepDD({ loan, setActive, reload, collection }) {
  const accounts = useMemo(() => loan?.bank_discovery?.accounts || [], [loan]);
  const [pick, setPick] = useState(accounts[0]?.account_number || "");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);

  if (loan.state === "dd_setup_pending") {
    return (
      <div className="text-center py-10" data-testid="step-dd-pending">
        <div className="w-16 h-16 rounded-full bg-[#FBF1DC] grid place-items-center mx-auto">
          <CircleNotch size={28} className="animate-spin text-[#B45309]" />
        </div>
        <h3 className="font-display text-2xl mt-5 font-medium">Verifying your Direct Debit</h3>
        <p className="text-sm text-[#4F5D4F] mt-2 max-w-md mx-auto">
          Mandate ref: <span className="font-mono">{loan.dd_account?.mandate_reference}</span> from <span className="font-mono">{loan.dd_account?.bank_name} ****{loan.dd_account?.account_number?.slice(-4)}</span>. Our team is verifying with the bank — disbursement follows.
        </p>
      </div>
    );
  }

  const submit = async () => {
    if (!pick || !ref) return toast.error("Pick an account and enter your DD mandate reference");
    setBusy(true);
    try {
      const acc = accounts.find((a) => a.account_number === pick);
      await api.post(`/loans/${loan.id}/dd-setup`, {
        borrower_account_number: pick,
        borrower_bank_code: acc.bank_code,
        dd_mandate_reference: ref,
      });
      toast.success("Direct Debit submitted for verification");
      reload();
      setActive({ stage: "dd_setup_pending", loan: { ...loan, state: "dd_setup_pending", dd_account: { ...acc, mandate_reference: ref } } });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="step-dd">
      <div className="bg-[#FBF1DC] border border-[#B45309]/40 rounded-2xl p-5 mb-5">
        <div className="flex items-center gap-2"><Warning size={16} weight="fill" className="text-[#B45309]" /><div className="overline !text-[#7a5a14]">Direct Debit setup required</div></div>
        <p className="text-sm text-[#7a5a14] mt-2">
          Set up an online Direct Debit / Standing Instruction from <strong>your own loan-disbursement bank account ONLY</strong> (no other account is accepted) to:
        </p>
        {collection && (
          <div className="mt-3 bg-white/60 rounded-xl p-3 grid sm:grid-cols-3 gap-2 text-xs font-mono">
            <div><div className="text-[#7a5a14] uppercase text-[10px] tracking-wider">Bank</div><div className="font-semibold">{collection.bank_name}</div></div>
            <div><div className="text-[#7a5a14] uppercase text-[10px] tracking-wider">Account name</div><div className="font-semibold">{collection.account_name}</div></div>
            <div><div className="text-[#7a5a14] uppercase text-[10px] tracking-wider">Account number</div><div className="font-semibold text-[#0A3622]">{collection.account_number}</div></div>
          </div>
        )}
        <p className="text-xs text-[#7a5a14] mt-3">After you set up the DD with your bank (USSD / app / branch), come back here and submit the mandate reference number to confirm.</p>
      </div>

      <div className="space-y-4">
        <div>
          <span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Your loan-disbursement account</span>
          <p className="text-xs text-[#4F5D4F] mt-1 mb-2">Pick the account you set up the Direct Debit from. Only your verified accounts (linked to your BVN/NIN) appear here.</p>
          <div className="space-y-2">
            {accounts.map((a) => (
              <label key={a.account_number} className={`block p-4 rounded-xl border cursor-pointer ${pick === a.account_number ? "border-[#0A3622] bg-[#E2F3EB]/30" : "border-[#E5E3DB] bg-white"}`} data-testid={`dd-account-${a.account_number}`}>
                <input type="radio" checked={pick === a.account_number} onChange={() => setPick(a.account_number)} className="mr-3 accent-[#0A3622]" />
                <span className="font-medium">{a.bank_name}</span>
                <span className="font-mono text-sm ml-2 text-[#4F5D4F]">****{a.account_number.slice(-4)}</span>
              </label>
            ))}
          </div>
        </div>
        <Field label="DD mandate reference (from your bank)">
          <input className="input mt-1 font-mono" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="DD-MAND-2026-001" data-testid="dd-ref-input" />
        </Field>
        <button onClick={submit} disabled={busy} className="btn-primary" data-testid="dd-submit-button">
          {busy ? "Submitting…" : <>Submit DD for verification <ArrowRight size={14} weight="bold" /></>}
        </button>
      </div>
    </div>
  );
}

function StepAwaitingDisburse({ loan }) {
  return (
    <div className="text-center py-10" data-testid="step-awaiting-disburse">
      <div className="w-16 h-16 rounded-full bg-[#E2F3EB] grid place-items-center mx-auto">
        <CheckCircle size={28} weight="fill" className="text-[#2D7A5D]" />
      </div>
      <h3 className="font-display text-2xl mt-5 font-medium">DD verified · awaiting disbursement</h3>
      <p className="text-sm text-[#4F5D4F] mt-2 max-w-md mx-auto">Your Direct Debit has been confirmed. Disbursement of <strong>{fmtNGN(loan.amount)}</strong> is queued and will reach you shortly.</p>
    </div>
  );
}

function StepDisbursed({ loan, setActive }) {
  const dest = loan.disbursed_to;
  return (
    <div className="text-center py-10" data-testid="step-disbursed">
      <div className="w-16 h-16 rounded-full bg-[#E2F3EB] grid place-items-center mx-auto">
        <Lightning size={28} weight="fill" className="text-[#2D7A5D]" />
      </div>
      <h3 className="font-display text-2xl mt-5 font-medium">Loan disbursed to school</h3>
      <p className="text-sm text-[#4F5D4F] mt-2 max-w-md mx-auto">
        {fmtNGN(loan.amount)} has been paid in full {dest?.school_name ? <>to <strong>{dest.school_name}</strong></> : "to the school"}
        {dest?.bank_name ? <> ({dest.bank_name} ****{dest.account_number?.slice(-4)})</> : ""}.
        First repayment due {fmtDate(loan.schedule?.[0]?.due_date)}.
      </p>
      <button onClick={() => setActive(null)} className="btn-primary mt-6" data-testid="disbursed-back">Back to loans</button>
    </div>
  );
}

function StepDeclined({ loan, setActive }) {
  return (
    <div className="text-center py-10" data-testid="step-declined">
      <div className="w-16 h-16 rounded-full bg-[#FBE3E3] grid place-items-center mx-auto">
        <Warning size={28} weight="fill" className="text-[#C93B3B]" />
      </div>
      <h3 className="font-display text-2xl mt-5 font-medium">Application declined</h3>
      <p className="text-sm text-[#4F5D4F] mt-2 max-w-md mx-auto">{(loan.ai_reasons || []).join(" · ")}</p>
      <button onClick={() => setActive(null)} className="btn-ghost mt-6">Back</button>
    </div>
  );
}

function Stat({ k, v, tone = "neutral", testId }) {
  const tones = { neutral: "bg-[#F9F7F4] text-[#132A13]", primary: "bg-[#0A3622] text-white", info: "bg-[#DCEAF3] text-[#2D6A8C]", success: "bg-[#E2F3EB] text-[#2D7A5D]" };
  return (
    <div className={`rounded-xl p-3 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-[0.14em] opacity-80 font-semibold leading-tight">{k}</div>
      <div className="font-display text-lg font-semibold mt-1">{v}</div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">{label}</span>{children}</label>;
}
